import React from 'react';
import styled from 'styled-components';
import { spacing } from '../../spacing';
import colors from '../../colors';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeSize = 'small' | 'medium' | 'large';

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: React.ReactNode;
  className?: string;
}

const variantStyles = {
  default: {
    background: colors.neutral[100],
    color: colors.neutral[700],
    border: colors.neutral[200],
  },
  primary: {
    background: colors.primary[100],
    color: colors.primary[700],
    border: colors.primary[200],
  },
  success: {
    background: '#F6FFED',
    color: colors.semantic.success.dark,
    border: colors.semantic.success.light,
  },
  warning: {
    background: '#FFFBE6',
    color: colors.semantic.warning.dark,
    border: colors.semantic.warning.light,
  },
  danger: {
    background: '#FFF1F0',
    color: colors.semantic.error.dark,
    border: colors.semantic.error.light,
  },
  info: {
    background: colors.primary[50],
    color: colors.primary[700],
    border: colors.primary[200],
  },
};

const sizeStyles = {
  small: {
    padding: `${spacing[2]} ${spacing[8]}`,
    fontSize: '12px',
    lineHeight: '16px',
  },
  medium: {
    padding: `${spacing[4]} ${spacing[8]}`,
    fontSize: '14px',
    lineHeight: '20px',
  },
  large: {
    padding: `${spacing[8]} ${spacing[12]}`,
    fontSize: '16px',
    lineHeight: '24px',
  },
};

const StyledBadge = styled.span<{ $variant: BadgeVariant; $size: BadgeSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  font-weight: 500;
  border: 1px solid;
  
  ${({ $variant }) => {
    const style = variantStyles[$variant] || variantStyles.default;
    return `
      background-color: ${style.background};
      color: ${style.color};
      border-color: ${style.border};
    `;
  }}
  
  ${({ $size }) => {
    const style = sizeStyles[$size] || sizeStyles.medium;
    return `
      padding: ${style.padding};
      font-size: ${style.fontSize};
      line-height: ${style.lineHeight};
    `;
  }}
`;

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'medium',
  children,
  className,
}) => {
  return (
    <StyledBadge $variant={variant} $size={size} className={className}>
      {children}
    </StyledBadge>
  );
};